import React from 'react';
import { Database, BarChart2, Search } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import { SEO } from '../../components/SEO';

export function LogAnalysis() {
  const { language } = useLanguage();

  const content = {
    hero: {
      title: {
        fr: 'Analyse des Logs',
        en: 'Log Analysis'
      },
      subtitle: {
        fr: 'Analyse approfondie des journaux de sécurité',
        en: 'In-depth analysis of security logs'
      }
    },
    features: [
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Collecte Centralisée',
          en: 'Centralized Collection'
        },
        description: {
          fr: 'Agrégation de tous les logs système',
          en: 'Aggregation of all system logs'
        }
      },
      {
        icon: <BarChart2 className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse en Temps Réel',
          en: 'Real-time Analysis'
        },
        description: {
          fr: 'Traitement instantané des données',
          en: 'Instant data processing'
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title={content.hero.title[language]}
        description={content.hero.subtitle[language]}
        keywords={['log analysis', 'security logs', 'SIEM']}
      />

      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Search className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Log Analysis Dashboard */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80" 
              alt="Log Analysis Dashboard"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Analyse en Temps Réel' : 'Real-time Analysis'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Visualisation et analyse instantanée des logs de sécurité'
                  : 'Instant visualization and analysis of security logs'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80" 
              alt="Security Metrics"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Métriques de Sécurité' : 'Security Metrics'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Suivi des indicateurs clés de sécurité'
                  : 'Tracking key security indicators'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 p-8 rounded-lg">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Dashboard View */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?auto=format&fit=crop&q=80" 
            alt="Advanced Analytics Dashboard"
            className="w-full h-96 object-cover"
          />
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'fr' ? 'Analyse Avancée' : 'Advanced Analytics'}
            </h3>
            <p className="text-gray-300">
              {language === 'fr' 
                ? 'Analyse approfondie des tendances et patterns de sécurité'
                : 'In-depth analysis of security trends and patterns'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}